import { useState } from "react";

// Components
import { FormattedMessage } from "react-intl";
import { Checkbox } from "components/Checkbox";
import CustomIcon from "components/CustomIcon";
import CheckboxLabel from "./components/CheckboxLabel/CheckboxLabel";
import { Box, Button, InputAdornment, TextField, Typography, Grid, IconButton, CircularProgress } from "@mui/material";

// Icons
import icons from "enums/icons";

// Utils
import theme from "assets/styles/themes";

// Types
import { TPerson } from "pages/BehavioralComparison/BehaviouralComparison.types";
import { RequestInfo } from "store/slice/store.types";
import { SerieLabel } from "store/slice/charts/chartsSlice.types";
import { CollapsableAssessments } from "../CollapsableAssessments";
import { IPaceAssessmentReportStatusAssessment } from "store/slice/Pace/paceSlice.types";

export type filteredPeople = {
    color: string;
    id: number;
    name: string;
    checked: boolean;
    teams: {
        name: string;
    }[];
    reports?:
        | (IPaceAssessmentReportStatusAssessment & {
              isCurrent: boolean;
              label: string;
              checked: boolean;
          })[]
        | undefined;
}[];

const PreviousPaceModal = ({
    people,
    fetchingReports,
    chartSeries,
    handleUpdateReports,
    onClose,
}: {
    people: TPerson[];
    fetchingReports: RequestInfo;
    chartSeries: SerieLabel[];
    handleUpdateReports: (checked: boolean, personId?: number, assessmentId?: number) => void;
    onClose: (shouldAddData?: boolean) => void;
}) => {
    const isLoading = fetchingReports === "fetching";
    const [inputNameValue, setInputNameValue] = useState<string>("");

    const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setInputNameValue(value);
    };

    const filteredPeople: filteredPeople = people
        .filter((person) => person.checked && person.name.toLowerCase().includes(inputNameValue.toLowerCase()))
        .map((person) => {
            const serie = chartSeries.find((serie) => serie.id === person.id);
            return {
                ...person,
                color: serie?.color ?? theme.palette.common.black,
            };
        });

    const allReports = people
        .filter((person) => person.checked && person?.reports)
        .flatMap((person) => person?.reports?.flatMap((r) => r.checked));

    const allSelected = allReports.every((value) => value);
    const someSelected = !allSelected && allReports.filter((value) => value).length > 0;

    return (
        <Box
            sx={{
                p: 2,
                zIndex: 3,
                height: 543,
                width: "100%",
                maxWidth: 970,
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.common.white,
            }}
        >
            {/* HEADER */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CustomIcon icon={icons.paceIconPrevious} sx={{ mr: 0.8 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        <FormattedMessage id="behaviouralComparison.previousPaceModal.title" />
                    </Typography>
                </Box>
                <IconButton onClick={() => onClose()}>
                    <CustomIcon icon={icons.crossClose} />
                </IconButton>
            </Box>

            {/* SEARCH BAR */}
            <Box mb={2}>
                <TextField
                    fullWidth
                    size="small"
                    id="team-search"
                    name="team-search"
                    autoComplete="off"
                    value={inputNameValue}
                    disabled={isLoading}
                    sx={{
                        "& .MuiInputBase-input": {
                            height: 22,
                        },
                        "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                            fontSize: 16,
                        },
                    }}
                    onChange={handleFilter}
                    placeholder="Filter users"
                    inputProps={{
                        maxLength: 30,
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <CustomIcon
                                    icon={icons.filter}
                                    sx={{ fontSize: "20px !important", color: theme.palette.secondary.light }}
                                />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* LIST */}
            {isLoading ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress color="primary" />
                </Box>
            ) : (
                <>
                    <Box mb={2}>
                        <CheckboxLabel
                            control={
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    size="small"
                                    onChange={(ev) => handleUpdateReports(ev.target.checked)}
                                />
                            }
                            label="Select all users"
                        />
                    </Box>
                    <Grid container spacing={1}>
                        {filteredPeople.map((person) => (
                            <Grid key={person.id} item sm={3}>
                                <CollapsableAssessments
                                    shouldExpand={filteredPeople.length > 4}
                                    person={person}
                                    handleUpdateReports={handleUpdateReports}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            <Box display="flex" alignItems="center" justifyContent="flex-end" mt="auto">
                <Button
                    size="small"
                    variant="text"
                    onClick={() => onClose()}
                    sx={{
                        minWidth: "auto",
                        "& .MuiButton-startIcon>*:nth-of-type(1)": { fontSize: 12 },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={isLoading}
                    onClick={() => onClose(true)}
                    startIcon={<CustomIcon icon={icons.check} />}
                    sx={{
                        minWidth: "auto",
                        marginLeft: 1,
                        "& .MuiButton-startIcon>*:nth-of-type(1)": { fontSize: 12 },
                    }}
                >
                    Add Data
                </Button>
            </Box>
        </Box>
    );
};

export default PreviousPaceModal;
